import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { BellRing } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCompactCurrency } from '@/lib/format'
import { ProgressBar } from '@/components/charts/progress-bar'
import { FloatingCard, PreviewFrame, Segmented } from '@/components/features/preview-frame'
import { BUDGETS, CHART_COLORS } from '@/constants/dashboard'

type PeriodKey = keyof typeof BUDGETS

const OPTIONS: Array<{ value: PeriodKey, label: string }> = [
    { value: 'current', label: 'Q4 to date' },
    { value: 'previous', label: 'Q3' }
]

export const BudgetPreview: FC = (): ReactNode => {
    const [period, setPeriod] = useState<PeriodKey>('current')
    const budgets = BUDGETS[period]
    const totalSpent = budgets.reduce((sum, item) => sum + item.spent, 0)
    const totalBudget = budgets.reduce((sum, item) => sum + item.budget, 0)

    return (
        <div className='relative'>
            <PreviewFrame>
                <div className='flex flex-wrap items-start justify-between gap-3'>
                    <div>
                        <p className='text-sm font-medium'>Budgets</p>
                        <p className='text-[11px] text-stone'>
                            {formatCompactCurrency(totalSpent)} of {formatCompactCurrency(totalBudget)} used across 5 teams
                        </p>
                    </div>

                    <Segmented label='Budget period' value={period} options={OPTIONS} onChange={setPeriod} />
                </div>

                <ul className='mt-6 space-y-5'>
                    {budgets.map((item, index) => {
                        const usage = item.spent / item.budget
                        const projectedRatio = item.projected / item.budget
                        const over = projectedRatio > 1
                        const variance = Math.round((projectedRatio - 1) * 100)

                        return (
                            <li key={item.team}>
                                <div className='mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs'>
                                    <span className='font-medium'>{item.team}</span>

                                    <span className='tabular-nums text-stone-dark'>
                                        {formatCompactCurrency(item.spent)}
                                        <span className='text-stone'> / {formatCompactCurrency(item.budget)}</span>
                                    </span>
                                </div>

                                <ProgressBar
                                    value={usage}
                                    marker={Math.min(1, projectedRatio)}
                                    size='md'
                                    color={over ? CHART_COLORS.terracotta : CHART_COLORS.burgundy}
                                    delay={index * 100}
                                    label={`${item.team} budget used`}
                                />

                                <p className={cn('mt-1.5 text-[11px]', over ? 'font-medium text-negative' : 'text-sage-dark')}>
                                    {period === 'previous'
                                        ? (item.spent > item.budget ? `Closed ${Math.round((usage - 1) * 100)}% over budget` : 'Closed within budget')
                                        : (over ? `Projected ${variance}% over by quarter end` : 'On track for quarter end')}
                                </p>
                            </li>
                        )
                    })}
                </ul>
            </PreviewFrame>

            <FloatingCard className='-right-4 -top-8 w-60 lg:-right-10'>
                <p className='flex items-center gap-2 text-xs font-medium'>
                    <span className='flex size-6 items-center justify-center rounded-full bg-terracotta/15 text-terracotta'>
                        <BellRing aria-hidden='true' className='size-3.5' />
                    </span>
                    Budget alert sent
                </p>

                <p className='mt-1.5 text-[11px] text-stone'>Marketing is pacing 9% over its Q4 budget. The owner was notified in Slack.</p>
            </FloatingCard>
        </div>
    )
}
